// src/Lobby.jsx - Enhanced with Better Visuals and Full Functionality
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import './Lobby.css';
import PageBG from './components/PageBG';

// Color palette - 20 vibrant colors
const COLOR_PALETTE = [
  '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316',
  '#eab308', '#06b6d4', '#ec4899', '#f59e0b', '#84cc16',
  '#8b5cf6', '#06d6a0', '#f43f5e', '#0ea5e9', '#d946ef',
  '#65a30d', '#dc2626', '#1d4ed8', '#059669', '#7c2d12'
];

export default function Lobby() {
  const { socket } = useOutletContext();
  const { lobbyId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [lobby, setLobby] = useState(null);
  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Color selection state
  const [selectedColor, setSelectedColor] = useState(null);
  const [playersReady, setPlayersReady] = useState({});
  const [playerColors, setPlayerColors] = useState({});

  // Join lobby and set up event listeners
  useEffect(() => {
    if (!user || !socket) return;

    console.log(`🏠 Joining lobby ${lobbyId}`);

    // Event handlers
    const handleLobbyUpdate = (updatedLobby) => {
      console.log('📡 Lobby update received:', updatedLobby);
      setLobby(updatedLobby);
    };

    const handleGameStarted = (data) => {
      console.log('🚀 Game started event received:', data);
      setIsStarting(false);
      // Navigation handled by App.jsx
    };

    const handlePlayerReadyUpdate = (data) => {
      console.log('✅ Player ready update:', data);
      setPlayersReady(data.playersReady || {});
      setPlayerColors(data.playerColors || {});
    };

    const handleColorConflict = (message) => {
      alert(message);
      setSelectedColor(null);
    };

    const handleLobbyCancelled = () => {
      console.log('❌ Lobby was cancelled');
      alert('The lobby has been cancelled by the host.');
      navigate('/dashboard');
    };

    // Set up event listeners
    socket.on('lobbyUpdate', handleLobbyUpdate);
    socket.on('gameStarted', handleGameStarted);
    socket.on('playerReadyUpdate', handlePlayerReadyUpdate);
    socket.on('colorConflict', handleColorConflict);
    socket.on('lobbyCancelled', handleLobbyCancelled);

    // Join the lobby
    socket.emit('joinLobby', lobbyId, (response) => {
      if (response.error) {
        console.error('❌ Failed to join lobby:', response.error);
        setError(response.error);
      } else {
        console.log('✅ Successfully joined lobby:', response);
        setLobby(response);
        // Set initial ready states if they exist
        if (response.playersReady) setPlayersReady(response.playersReady);
        if (response.playerColors) setPlayerColors(response.playerColors);
      }
    });

    // Leave lobby on unmount
    return () => {
      console.log('Leaving lobby:', lobbyId);
      socket.emit('leaveLobby', lobbyId);
      socket.off('lobbyUpdate', handleLobbyUpdate);
      socket.off('gameStarted', handleGameStarted);
      socket.off('playerReadyUpdate', handlePlayerReadyUpdate);
      socket.off('colorConflict', handleColorConflict);
      socket.off('lobbyCancelled', handleLobbyCancelled);
    };
  }, [lobbyId, navigate, user, socket]);

  const handleStartGame = () => {
    if (!socket || !lobbyId) return;
    
    console.log(`🎮 Starting game for lobby ${lobbyId}`);
    setIsStarting(true);
    socket.emit('startGame', lobbyId);
  };

  const handleLeaveLobby = () => {
    socket.emit('leaveLobby', lobbyId);
    navigate('/dashboard');
  };

  const copyLobbyId = async () => {
    try {
      await navigator.clipboard.writeText(lobbyId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = lobbyId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleColorSelect = (color) => {
    const takenColors = Object.values(playerColors);
    if (takenColors.includes(color) && playerColors[user.id] !== color) {
      alert('This color is already taken!');
      return;
    }

    setSelectedColor(color);
    socket.emit('selectColor', { lobbyId: lobby.id, color });
  };

  const handleReadyToggle = () => {
    if (!selectedColor && !playerColors[user.id]) {
      alert('Please select a color first!');
      return;
    }

    const currentColor = selectedColor || playerColors[user.id];
    const newReadyState = !playersReady[user.id];
    
    socket.emit('toggleReady', { 
      lobbyId: lobby.id, 
      ready: newReadyState, 
      color: currentColor 
    });
  };

  // Error state
  if (error) {
    return (
      <PageBG src="/bg/anticipation.jpg">
        <div className="lobby-wrapper">
          <div className="lobby-container">
            <div className="error-card">
              <div className="error-icon">⚠️</div>
              <h2>Connection Error</h2>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={handleLeaveLobby}>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </PageBG>
    );
  }

  // Loading state
  if (!lobby || !user) {
    return (
      <PageBG src="/bg/anticipation.jpg">
        <div className="lobby-wrapper">
          <div className="lobby-container">
            <div className="loading-card">
              <div className="loading-spinner"></div>
              <h2>Joining Lobby</h2>
              <p>Connecting to lobby {lobbyId}</p>
            </div>
          </div>
        </div>
      </PageBG>
    );
  }

  const isHost = user.id === lobby.host.id;
  const isLobbyFull = lobby.players.length === lobby.rules.players;
  const myColor = playerColors[user.id] || selectedColor;
  const isReady = playersReady[user.id] || false;
  
  // Check if all players are ready
  const allPlayersReady = lobby.players.every(player => 
    playersReady[player.id] && playerColors[player.id]
  );

  return (
    <PageBG src="/bg/anticipation.jpg">
      <div className="lobby-wrapper">
        <div className="lobby-container">
          {/* Header Section */}
          <div className="lobby-header">
            <div className="lobby-title-section">
              <div className="game-type-badge">
                {lobby.rules.gameType === 'ranked' ? '🏆' : '🎮'} {lobby.rules.gameType || 'casual'}
              </div>
              <h1>Game Lobby</h1>
              <div className="lobby-status">
                {isLobbyFull && allPlayersReady ? (
                  <span className="status-ready">✅ Ready to Start</span>
                ) : isLobbyFull ? (
                  <span className="status-waiting">⏳ Waiting for Ready</span>
                ) : (
                  <span className="status-waiting">⏳ Waiting for Players</span>
                )}
              </div>
            </div>
            <button className="btn btn-secondary btn-leave" onClick={handleLeaveLobby}>
              Leave Lobby
            </button>
          </div>

          {/* Game Settings Card */}
          <div className="settings-card">
            <h3>Game Settings</h3>
            <div className="settings-grid">
              <div className="setting-item">
                <span className="setting-label">Lobby ID</span>
                <div className="lobby-id-container">
                  <code className="lobby-id">{lobby.id}</code>
                  <button 
                    className={`btn-copy ${copied ? 'copied' : ''}`}
                    onClick={copyLobbyId}
                    title="Copy to clipboard"
                  >
                    {copied ? '✔' : '📋'}
                  </button>
                </div>
              </div>
              <div className="setting-item">
                <span className="setting-label">Privacy</span>
                <span className={`privacy-badge privacy-${lobby.rules.privacy}`}>
                  {lobby.rules.privacy === 'public' ? '🌐' : lobby.rules.privacy === 'friends' ? '👥' : '🔒'} 
                  {' ' + lobby.rules.privacy}
                </span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Pucks per Turn</span>
                <span className="setting-value">{lobby.rules.pucksPerTurn}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Game Mode</span>
                <span className="setting-value">Real-time Multiplayer</span>
              </div>
            </div>
          </div>

          {/* Players Section */}
          <div className="players-section">
            <div className="players-header">
              <h3>Players ({lobby.players.length}/{lobby.rules.players})</h3>
              {!isLobbyFull && (
                <div className="invite-hint">
                  Share lobby ID: <strong>{lobby.id}</strong>
                </div>
              )}
            </div>
            
            <div className="players-grid">
              {lobby.players.map((player, index) => {
                const playerReady = playersReady[player.id];
                const playerColor = playerColors[player.id];
                const isMe = player.id === user.id;
                
                return (
                  <div key={player.id} className="player-card filled">
                    <div 
                      className="player-avatar" 
                      style={{
                        backgroundColor: playerColor || '#374151',
                        border: playerColor ? '3px solid rgba(255, 255, 255, 0.3)' : '3px solid #4b5563'
                      }}
                    >
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="player-info">
                      <div className="player-name">
                        {player.name}
                        {lobby.host.id === player.id && (
                          <span className="host-badge">👑 Host</span>
                        )}
                        {isMe && (
                          <span className="you-badge">You</span>
                        )}
                      </div>
                      <div className="player-status">
                        {playerColor ? (
                          playerReady ? (
                            <>
                              <span className="status-dot ready"></span>
                              <span style={{ color: '#22c55e' }}>Ready</span>
                            </>
                          ) : (
                            <>
                              <span className="status-dot waiting"></span>
                              <span>Choosing...</span>
                            </>
                          )
                        ) : (
                          <>
                            <span className="status-dot waiting"></span>
                            <span>No Color</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Empty slots */}
              {Array.from({ length: lobby.rules.players - lobby.players.length }).map((_, index) => (
                <div key={`empty-${index}`} className="player-card empty">
                  <div className="player-avatar empty-avatar">
                    +
                  </div>
                  <div className="player-info">
                    <div className="player-name">Waiting for player...</div>
                    <div className="player-status">
                      <span className="status-dot waiting"></span>
                      Open Slot
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Color Selection Section */}
          <div className="settings-card">
            <h3>Choose Your Color</h3>
            
            {/* Color Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(10, 1fr)',
              gap: '0.75rem',
              marginBottom: '1.5rem',
              padding: '0.5rem',
              justifyItems: 'center'
            }}>
              {COLOR_PALETTE.map((color, index) => {
                const isTaken = Object.values(playerColors).includes(color) && playerColors[user.id] !== color;
                const isSelected = myColor === color;
                
                return (
                  <button
                    key={index}
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      backgroundColor: color,
                      border: isSelected ? '4px solid #60a5fa' : '2px solid rgba(255,255,255,0.2)',
                      cursor: isTaken ? 'not-allowed' : 'pointer',
                      opacity: isTaken ? 0.3 : 1,
                      transform: isSelected ? 'scale(1.15)' : 'scale(1)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      boxShadow: isSelected ? '0 0 20px rgba(96, 165, 250, 0.5)' : '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                    onClick={() => !isTaken && handleColorSelect(color)}
                    disabled={isTaken}
                    title={isTaken ? 'Color taken' : 'Select this color'}
                    onMouseEnter={(e) => !isTaken && !isSelected && (e.target.style.transform = 'scale(1.1)')}
                    onMouseLeave={(e) => !isTaken && !isSelected && (e.target.style.transform = 'scale(1)')}
                  >
                    {isSelected && (
                      <span style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '20px',
                        textShadow: '0 0 4px rgba(0,0,0,0.8)'
                      }}>
                        ✔
                      </span>
                    )}
                    {isTaken && (
                      <span style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '20px',
                        textShadow: '0 0 4px rgba(0,0,0,0.8)'
                      }}>
                        ✗
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected Color Preview */}
            {myColor && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem',
                fontSize: '1.1rem',
                color: '#e5e7eb',
                fontWeight: '500'
              }}>
                <span>Your color:</span>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: myColor,
                  border: '3px solid rgba(255,255,255,0.4)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }} />
              </div>
            )}

            {/* Ready Button */}
            <div style={{ textAlign: 'center' }}>
              <button
                className={`btn ${isReady ? 'btn-success' : 'btn-primary'} btn-large`}
                onClick={handleReadyToggle}
                disabled={!myColor}
                style={{
                  padding: '0.75rem 3rem',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  backgroundColor: isReady ? '#059669' : undefined,
                  minWidth: '200px'
                }}
              >
                {isReady ? '✔ Ready!' : 'Ready Up'}
              </button>
            </div>
          </div>

          {/* --- ACTION SECTION (CENTERED) --- */}
          <div 
            className="action-section" 
            style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              textAlign: 'center',
              minHeight: '120px' // Give it some space
            }}
          >
            {isHost && isLobbyFull && !isStarting && allPlayersReady && (
              <button 
                className="btn btn-primary btn-large btn-start-game" 
                onClick={handleStartGame}
                style={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  fontSize: '1.3rem',
                  padding: '1rem 3rem',
                  fontWeight: 'bold'
                }}
              >
                🚀 Start Multiplayer Game
              </button>
            )}
            
            {isHost && (!isLobbyFull || !allPlayersReady) && (
              <div className="waiting-state">
                <p className="waiting-text" style={{ fontSize: '1.1rem', marginTop: '1rem' }}>
                  {!isLobbyFull 
                    ? `Need ${lobby.rules.players - lobby.players.length} more player${lobby.rules.players - lobby.players.length > 1 ? 's' : ''} to start`
                    : 'Waiting for all players to select colors and ready up'
                  }
                </p>
              </div>
            )}
            
            {!isHost && !isStarting && (
              <div className="waiting-state">
                <p className="waiting-text" style={{ fontSize: '1.1rem', color: '#9ca3af' }}>
                  {allPlayersReady 
                    ? `Waiting for ${lobby.host.name} to start the game...`
                    : 'Select your color and ready up!'}
                </p>
              </div>
            )}
            
            {isStarting && (
              <div className="starting-state">
                <div className="loading-spinner large"></div>
                <h3>Starting Game...</h3>
                <p>Initializing multiplayer game server...</p>
              </div>
            )}
          </div>

          {/* How to Play Section */}
          <div className="info-section">
            <h3>How Multiplayer Works</h3>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-icon">🔄</div>
                <div className="info-content">
                  <strong>Real-time synchronization</strong>
                  <span>All players see the same game state instantly</span>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">⏰</div>
                <div className="info-content">
                  <strong>Turn-based gameplay</strong>
                  <span>Take turns placing and shooting pucks</span>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🎮</div>
                <div className="info-content">
                  <strong>Live physics</strong>
                  <span>Watch pucks move in real-time across all devices</span>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">⏱️</div>
                <div className="info-content">
                  <strong>Move timer</strong>
                  <span>15 seconds per turn to keep games moving</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageBG>
  );
}
